import { useEffect, useRef, type MouseEvent, type ReactNode } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Skeleton, type DataTableColumn, type TableSort } from "@h2j-data-team/ui";

import { cn } from "@/lib/utils";

/*
 * The one list that stays in the app instead of the library's DataTable: a run
 * can hold hundreds of thousands of files, so only the rows in view (+ overscan)
 * are mounted, with `@tanstack/react-virtual`. Rows are divs, so the ARIA table
 * roles carry the semantics a <table> would, and the look follows the library's
 * table: head and row heights, cell padding and type size come from the same
 * `--h2j-table-*` tokens, so they follow the density preference and line up with
 * the DataTable on the other screens.
 *
 * The columns are the library's `DataTableColumn`, so one definition can feed
 * both this list and `localTablePage` (filter, search and sort of the rows held
 * in memory). Row heights may vary and are measured; `estimateSize` is only the
 * first guess.
 */

// Two cell paddings of the library's table, so cells keep its spacing.
const COLUMN_GAP = "1.5rem";
const FLEX_COLUMN_MIN = "14rem";

const size = (value: number | string) => (typeof value === "number" ? `${value}px` : value);

function gridTemplate<T>(columns: readonly DataTableColumn<T>[]) {
  return columns
    .map((c) => (c.width === undefined ? `minmax(${FLEX_COLUMN_MIN},1fr)` : size(c.width)))
    .join(" ");
}

// Below this width the list scrolls sideways instead of squeezing the columns.
function minWidth<T>(columns: readonly DataTableColumn<T>[]) {
  const sizes = columns.map((c) => (c.width === undefined ? FLEX_COLUMN_MIN : size(c.width)));
  return `calc(${sizes.join(" + ")} + ${columns.length - 1} * ${COLUMN_GAP} + 1.5rem)`;
}

function SortButton({
  direction,
  onClick,
  children,
}: {
  direction?: TableSort["direction"];
  onClick: () => void;
  children: ReactNode;
}) {
  const Icon = direction === "asc" ? ArrowUp : direction === "desc" ? ArrowDown : ChevronsUpDown;
  return (
    <button
      type="button"
      onClick={onClick}
      data-active={direction ? "" : undefined}
      className="group/sort inline-flex max-w-full items-center gap-1 rounded-sm uppercase tracking-[inherit] outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring data-active:font-semibold data-active:text-ink"
    >
      <span className="min-w-0 truncate">{children}</span>
      <Icon
        aria-hidden
        className="size-3 shrink-0 opacity-0 group-hover/sort:opacity-100 group-focus-visible/sort:opacity-100 group-data-active/sort:opacity-100"
      />
    </button>
  );
}

// A click anywhere on the row opens it through the control inside the cell
// marked `[data-row-link]`, as the library's `rowLink` does: clicks on controls
// and text selections are left alone, and the keyboard keeps using the control.
function openRow(event: MouseEvent<HTMLElement>) {
  const row = event.currentTarget;
  const target = event.target as HTMLElement;
  if (!row.contains(target)) return;
  const control = target.closest("a, button, input, select, textarea, label, [tabindex]");
  if (event.defaultPrevented || (control && row.contains(control))) return;
  if (row.ownerDocument.getSelection()?.isCollapsed === false) return;
  row.querySelector<HTMLElement>("[data-row-link] :is(a[href], button)")?.click();
}

export function VirtualRowList<T>({
  label,
  columns,
  rows,
  getKey,
  sort,
  onSort,
  linked,
  showHeader = true,
  estimateSize = 36,
  overscan = 10,
  onReachEnd,
  loadingMoreLabel,
  className,
}: {
  /** Name of the list and of its scrollable region. */
  label: string;
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  getKey: (row: T) => string;
  /** The active order, for `aria-sort` and the arrow of the sorted column. */
  sort?: TableSort;
  /** A column with `sort` was activated; the host decides the next order. */
  onSort?: (column: DataTableColumn<T>) => void;
  /** Rows whose click is forwarded to the `rowLink` control of their cell. */
  linked?: (row: T) => boolean;
  /** Off for a log stream: rows are list items with no column titles. */
  showHeader?: boolean;
  estimateSize?: number;
  overscan?: number;
  /** Fires as the last row scrolls into view (the files list pulls its next page). */
  onReachEnd?: () => void;
  /** While set, a placeholder row and a status announce the page being fetched. */
  loadingMoreLabel?: string;
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    getItemKey: (index) => getKey(rows[index]!),
    estimateSize: () => estimateSize,
    overscan,
  });

  const items = virtualizer.getVirtualItems();
  const lastIndex = items.length > 0 ? items[items.length - 1]!.index : -1;

  // Keep the callback in a ref so the end-of-list effect depends only on the
  // scroll position, not on a fresh inline closure each render.
  const onReachEndRef = useRef(onReachEnd);
  useEffect(() => {
    onReachEndRef.current = onReachEnd;
  });
  useEffect(() => {
    if (lastIndex >= 0 && lastIndex >= rows.length - 1) onReachEndRef.current?.();
  }, [lastIndex, rows.length]);

  const template = gridTemplate(columns);

  return (
    <div
      ref={scrollRef}
      role="region"
      aria-label={label}
      aria-busy={loadingMoreLabel ? true : undefined}
      tabIndex={0}
      className={cn(
        "max-h-[70vh] overflow-auto rounded-lg border border-hairline bg-surface -outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring",
        className,
      )}
    >
      <div
        role={showHeader ? "table" : "list"}
        aria-label={label}
        aria-rowcount={showHeader ? rows.length + 1 : undefined}
        className="relative text-(length:--h2j-table-font-size) leading-(--h2j-table-line-height) text-ink"
        style={{ minWidth: minWidth(columns) }}
      >
        {showHeader && (
          // Sticks inside the scroll region, so it scrolls sideways with the rows
          // and shares their width, scrollbar included.
          <div
            role="row"
            aria-rowindex={1}
            className="sticky top-0 z-1 grid h-(--h2j-table-head-height) items-center gap-x-6 border-b border-hairline bg-surface-muted px-3 text-xs font-medium uppercase tracking-[0.025em] text-faint"
            style={{ gridTemplateColumns: template }}
          >
            {columns.map((column) => {
              const active = sort?.id === column.id ? sort.direction : undefined;
              return (
                <div
                  key={column.id}
                  role="columnheader"
                  aria-sort={
                    active === "asc" ? "ascending" : active === "desc" ? "descending" : undefined
                  }
                  className={cn("min-w-0 truncate", column.align === "end" && "text-right")}
                >
                  {column.sort && onSort ? (
                    <SortButton direction={active} onClick={() => onSort(column)}>
                      {column.header}
                    </SortButton>
                  ) : (
                    column.header
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ height: virtualizer.getTotalSize() }} className="relative w-full">
          {items.map((vi) => {
            const row = rows[vi.index]!;
            const isLinked = linked?.(row) ?? false;
            return (
              <div
                key={getKey(row)}
                role={showHeader ? "row" : "listitem"}
                // 1-based, and the head is row 1.
                aria-rowindex={showHeader ? vi.index + 2 : undefined}
                data-index={vi.index}
                data-linked={isLinked ? "" : undefined}
                ref={virtualizer.measureElement}
                onClick={isLinked ? openRow : undefined}
                className="absolute top-0 left-0 grid min-h-(--tbl-row-min) w-full items-baseline gap-x-6 border-b border-hairline-soft px-3 py-(--tbl-cell-py) hover:bg-surface-muted/60 data-linked:cursor-pointer"
                style={{
                  gridTemplateColumns: template,
                  transform: `translateY(${vi.start}px)`,
                }}
              >
                {columns.map((column) => (
                  <div
                    key={column.id}
                    role={showHeader ? (column.rowHeader ? "rowheader" : "cell") : undefined}
                    data-row-link={column.rowLink ? "" : undefined}
                    className={cn(
                      "min-w-0",
                      column.wrap
                        ? "wrap-anywhere"
                        : "overflow-hidden text-ellipsis whitespace-nowrap",
                      column.variant === "numeric" && "font-mono tabular-nums",
                      column.align === "end" && "text-right",
                    )}
                  >
                    {column.cell(row)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {loadingMoreLabel && (
          <>
            <div aria-hidden className="p-3">
              <Skeleton className="h-6 w-full" />
            </div>
            <p role="status" className="sr-only">
              {loadingMoreLabel}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
